import { bus } from '@/core/EventBus';
import type { DialogueRunner } from '@/gameplay/Dialogue';

export class DialoguePanel {
  readonly #root: HTMLDivElement;
  readonly #speaker: HTMLDivElement;
  readonly #text: HTMLDivElement;
  readonly #choices: HTMLDivElement;
  readonly #hint: HTMLDivElement;
  readonly #unsubscribers: Array<() => void> = [];

  constructor(container: HTMLElement, private readonly runner: DialogueRunner) {
    this.#root = document.createElement('div');
    this.#root.className = 'na-dialogue';
    this.#root.innerHTML = `
      <section class="na-dialogue-panel" aria-live="polite">
        <div class="na-dialogue-speaker"></div>
        <div class="na-dialogue-text"></div>
        <div class="na-dialogue-choices"></div>
        <div class="na-dialogue-hint"></div>
      </section>
    `;
    installDialogueStyles(container.ownerDocument);
    container.appendChild(this.#root);
    this.#speaker = this.#query('.na-dialogue-speaker');
    this.#text = this.#query('.na-dialogue-text');
    this.#choices = this.#query('.na-dialogue-choices');
    this.#hint = this.#query('.na-dialogue-hint');
    this.#wireEvents();
    this.update();
  }

  handleKey(key: string): boolean {
    if (!this.runner.active) return false;
    const node = this.runner.currentNode;
    if (!node) return false;
    if (key === 'enter' && (!node.choices || node.choices.length === 0)) {
      this.runner.continue();
      this.update();
      return true;
    }
    const index = Number(key) - 1;
    if (Number.isInteger(index) && index >= 0 && index < (node.choices?.length ?? 0)) {
      this.runner.choose(index);
      this.update();
      return true;
    }
    return false;
  }

  update(): void {
    const node = this.runner.currentNode;
    this.#root.classList.toggle('is-visible', this.runner.active && node !== null);
    this.#choices.replaceChildren();
    if (!node) {
      this.#speaker.textContent = '';
      this.#text.textContent = '';
      this.#hint.textContent = '';
      return;
    }

    this.#speaker.textContent = node.speaker ?? '';
    this.#text.textContent = node.text;
    const choices = node.choices ?? [];
    if (choices.length === 0) {
      this.#hint.textContent = 'Enter';
      return;
    }

    this.#hint.textContent = '1-9';
    choices.forEach((choice, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'na-dialogue-choice';
      button.textContent = `${index + 1}. ${choice.label}`;
      button.addEventListener('click', () => {
        this.runner.choose(index);
        this.update();
      });
      this.#choices.appendChild(button);
    });
  }

  dispose(): void {
    for (const unsubscribe of this.#unsubscribers) unsubscribe();
    this.#unsubscribers.length = 0;
    this.#root.remove();
  }

  #wireEvents(): void {
    this.#unsubscribers.push(
      bus.on('dialogue:started', () => this.update()),
      bus.on('dialogue:nodeChanged', () => this.update()),
      bus.on('dialogue:choiceSelected', () => this.update()),
      bus.on('dialogue:ended', () => this.update()),
    );
  }

  #query<T extends HTMLElement>(selector: string): T {
    const found = this.#root.querySelector<T>(selector);
    if (!found) throw new Error(`Dialogue element missing: ${selector}`);
    return found;
  }
}

function installDialogueStyles(document: Document): void {
  if (document.getElementById('na-dialogue-styles')) return;
  const style = document.createElement('style');
  style.id = 'na-dialogue-styles';
  style.textContent = `
    .na-dialogue {
      position: absolute;
      inset: 0;
      display: grid;
      align-items: end;
      justify-items: center;
      padding: 18px;
      pointer-events: none;
      opacity: 0;
      color: #e8ddc8;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      transition: opacity 140ms ease;
    }
    .na-dialogue.is-visible {
      opacity: 1;
      pointer-events: auto;
    }
    .na-dialogue-panel {
      width: min(760px, calc(100vw - 36px));
      min-height: 150px;
      padding: 14px 16px;
      border-left: 3px solid #e0951c;
      background: rgba(8, 8, 10, 0.84);
      box-shadow: 0 12px 34px rgba(0, 0, 0, 0.42);
    }
    .na-dialogue-speaker {
      min-height: 16px;
      color: #e0951c;
      font-size: 12px;
      line-height: 1;
      text-transform: uppercase;
    }
    .na-dialogue-text {
      margin-top: 8px;
      font-size: 15px;
      line-height: 1.38;
    }
    .na-dialogue-choices {
      display: grid;
      gap: 7px;
      margin-top: 14px;
    }
    .na-dialogue-choice {
      min-height: 34px;
      padding: 8px 10px;
      border: 1px solid rgba(232, 221, 200, 0.24);
      background: rgba(39, 40, 34, 0.74);
      color: #e8ddc8;
      font: inherit;
      font-size: 13px;
      text-align: left;
      cursor: pointer;
    }
    .na-dialogue-choice:hover,
    .na-dialogue-choice:focus-visible {
      border-color: rgba(224, 149, 28, 0.86);
      outline: 2px solid rgba(224, 149, 28, 0.22);
      outline-offset: 2px;
    }
    .na-dialogue-hint {
      margin-top: 10px;
      color: rgba(232, 221, 200, 0.62);
      font-size: 11px;
      text-transform: uppercase;
    }
  `;
  document.head.appendChild(style);
}
